import React from 'react';
import { Breakpoint } from '@mui/material';
import { ImageAsset } from '@youhelp/common/model/resource/image';
import { breakpoints, buildDefaultImageReponsiveGrid } from '../components/image/utils';
import { ImageResponsiveGrid } from '../components/image/model';
import { useImageFormat } from './useImageFormat';

type BackgroundImageDictionary = Partial<Record<Breakpoint, string>>;

export const useBackgroundImage = ({
  asset,
  responsiveGrid,
}: {
  asset?: ImageAsset;
  responsiveGrid?: ImageResponsiveGrid;
}): BackgroundImageDictionary | undefined => {
  const format = useImageFormat();

  return React.useMemo(() => {
    if (!format || !asset) {
      return;
    }

    const assetItem = format === 'webp' && asset.webp ? asset.webp : asset.base;
    const grid = responsiveGrid ?? buildDefaultImageReponsiveGrid(assetItem);

    const preparedGridEntries = Object.entries(grid).sort((a, b) => breakpoints.indexOf(a[0]) - breakpoints.indexOf(b[0]));

    const backgroundImage: BackgroundImageDictionary = {};

    if (!assetItem.images?.length) {
      return;
    }

    for (const [bp, width] of preparedGridEntries) {
      const path = assetItem.images?.find(({ width: imgWidth }) => width === imgWidth)?.path;

      if (!path) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`Image with width ${width} not found inside image asset "${assetItem.src}".`);
        }
        continue;
      }

      backgroundImage[bp] = `url(${path})`;
    }

    return backgroundImage;
  }, [asset, format, responsiveGrid]);
};
